import { useEffect, useMemo, useState } from 'react';
import { Box, Chip, Grid, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import PercentIcon from '@mui/icons-material/Percent';

import PageHeader from '../../../components/PageHeader/PageHeader';
import MonthYearSelector from '../../../components/Reports/Sales/MonthYearSelector';
import ReportCurrencyPicker from '../../../components/Currency/ReportCurrencyPicker';
import useReportCurrency from '../../../hooks/useReportCurrency';
import { getSummaryDashboardData } from '../../../services/ReportsSummaryService';
import { formatMoney } from '../../../utils/currency';

const money = (value = 0) => `${formatMoney(Number(value || 0))}`;
const percent = (value = 0) => `${Number(value || 0).toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
const gridValue = (valueOrParams) => (valueOrParams && typeof valueOrParams === 'object' && 'value' in valueOrParams
  ? valueOrParams.value
  : valueOrParams);

const CHANNEL_LABELS = {
  LOCAL: 'Local',
  WEB: 'Web',
  PEDIDOS_YA: 'Pedidos Ya',
  B2B: 'Pedidos B2B',
  TOTAL: 'Total',
};

const sectionTitle = (section) => {
  if (section.type === 'B2B') return 'B2B';
  return section.name;
};

const profitColor = (value) => (Number(value || 0) < 0 ? '#d32f2f' : '#2e7d32');

const KpiCard = ({ title, value, icon, color, loading, delta }) => (
  <Paper elevation={2} sx={{ minHeight: 94, height: '100%', p: 2, borderRadius: 1, overflow: 'hidden' }}>
    {loading ? (
      <Stack spacing={1}>
        <Skeleton width="45%" />
        <Skeleton width="70%" height={34} />
      </Stack>
    ) : (
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary" fontWeight={700}>{title}</Typography>
          <Box sx={{ color, display: 'flex' }}>{icon}</Box>
        </Stack>
        <Typography variant="h5" fontWeight={700} sx={{ color, lineHeight: 1.15 }}>{value}</Typography>
        {delta !== undefined && (
          <Chip
            size="small"
            variant="outlined"
            color={delta >= 0 ? 'success' : 'error'}
            icon={delta >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
            label={`${delta >= 0 ? '+' : ''}${money(delta)} vs mes anterior`}
            sx={{ alignSelf: 'flex-start', maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
          />
        )}
      </Stack>
    )}
  </Paper>
);

const SummaryReports = () => {
  const [reportCurrency, setReportCurrency] = useReportCurrency();
  const [month, setMonth] = useState(dayjs().month() + 1);
  const [year, setYear] = useState(dayjs().year());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;
    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await getSummaryDashboardData({ month, year });
        if (!ignore) setData(result);
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    fetchData();
    return () => { ignore = true; };
  }, [month, year, reportCurrency]);

  const total = data?.total || {};
  const previousTotal = data?.previousTotal || {};

  const sections = useMemo(() => (data?.sections || []).map((section) => ({
    ...section,
    key: `${section.type}-${section.id}`,
    title: sectionTitle(section),
    rows: [
      ...section.channels.map((row) => ({ ...row, label: row.label ? `Franquicia ${row.label}` : (CHANNEL_LABELS[row.channel] || row.channel) })),
      ...(section.channels.length > 1 ? [{ ...section.total, label: CHANNEL_LABELS.TOTAL }] : []),
    ],
  })), [data]);

  const columns = useMemo(() => {
    const numeric = { type: 'number', align: 'right', headerAlign: 'right' };
    return [
      { field: 'label', headerName: 'Canal', flex: 1, minWidth: 160 },
      { field: 'orderCount', headerName: 'Ventas', width: 100, ...numeric },
      { field: 'netSales', headerName: 'Vendido', width: 150, valueFormatter: (p) => money(gridValue(p)), ...numeric },
      { field: 'costOfGoodsSold', headerName: 'Costo', width: 150, valueFormatter: (p) => money(gridValue(p)), ...numeric },
      {
        field: 'grossProfit',
        headerName: 'Margen',
        width: 150,
        ...numeric,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: '100%', width: '100%' }}>
            <Typography variant="body2" fontWeight={600} sx={{ color: profitColor(params.value) }}>{money(params.value)}</Typography>
          </Box>
        ),
      },
      {
        field: 'grossMarginPercent',
        headerName: 'Margen %',
        width: 120,
        ...numeric,
        renderCell: (params) => (Number(params.row.netSales || 0) === 0 ? '—' : percent(params.value)),
      },
    ];
  }, []);

  return (
    <Box p={3}>
      <Grid container spacing={2} alignItems="center" justifyContent="space-between">
        <Grid item>
          <PageHeader title="Resumen" subtitle="Cómo le fue al negocio en el mes, por canal de venta" />
          <ReportCurrencyPicker currency={reportCurrency} onChange={setReportCurrency} />
        </Grid>
        <Grid item xs={12} md="auto">
          <Box display="flex" alignItems="center" gap={2} justifyContent="flex-end">
            <MonthYearSelector
              month={month}
              setMonth={setMonth}
              year={year}
              setYear={setYear}
              sx={{ width: { xs: '100%', sm: 300 } }}
            />
          </Box>
        </Grid>
      </Grid>

      <Grid container spacing={2} mt={1} alignItems="flex-start">
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Vendido"
            value={money(total.netSales)}
            icon={<MonetizationOnIcon />}
            color="#1976d2"
            loading={loading}
            delta={Number(((total.netSales || 0) - (previousTotal.netSales || 0)).toFixed(2))}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard title="Costo" value={money(total.costOfGoodsSold)} icon={<Inventory2Icon />} color="#455a64" loading={loading} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Margen"
            value={money(total.grossProfit)}
            icon={<TrendingUpIcon />}
            color={profitColor(total.grossProfit)}
            loading={loading}
            delta={Number(((total.grossProfit || 0) - (previousTotal.grossProfit || 0)).toFixed(2))}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard title="Margen %" value={percent(total.grossMarginPercent)} icon={<PercentIcon />} color={profitColor(total.grossProfit)} loading={loading} />
        </Grid>
      </Grid>

      {loading && sections.length === 0 && <Skeleton variant="rounded" height={160} sx={{ mt: 2 }} />}
      {!loading && sections.length === 0 && (
        <Paper elevation={1} sx={{ p: 2, mt: 2, borderRadius: 1 }}>
          <Typography color="text.secondary">No hay ventas en este mes</Typography>
        </Paper>
      )}
      {sections.map((section) => (
        <Paper key={section.key} elevation={1} sx={{ p: 2, mt: 2, borderRadius: 1 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>{section.title}</Typography>
          <DataGrid
            autoHeight
            rows={section.rows}
            columns={columns}
            getRowId={(row) => row.channel}
            loading={loading}
            hideFooter
            disableRowSelectionOnClick
            getRowClassName={(params) => (params.row.channel === 'TOTAL' ? 'summary-total-row' : '')}
            sx={{ '& .summary-total-row': { fontWeight: 700, backgroundColor: 'action.hover' } }}
          />
        </Paper>
      ))}
      <Box sx={{ mb: 5 }} />
    </Box>
  );
};

export default SummaryReports;
